/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

// pcg-report core: display-value/label selection (no HTML).
// Source: cardiac_report_v0_5_6_dev_2.html lines 5344-5367 (observed display),
// 5430-5462 (reportCardTitle, reportResultText, providerFindingFlagText),
// 5523-5532 (displayReferenceRange).
// Deviations: currentHeartRateBpm() -> hrBpm arg; window.currentViewMode/adminMode
// globals -> explicit view options.

import { cardiacCycleRatioZonesForHeartRate, expectedSysDiaRangesForHeartRate } from "./zones"
import {
  heartRateRangeOf,
  isCardiacCycleTimingFeature,
  isSysDiaDurationFeature,
  isRhythmRegularityFeature,
} from "./features"
import { timingSummaryResultLabel, rhythmRegularityDisplayLabel } from "./timing-pattern"
import { getStatusLabel, heartRateRangeGrades, structuralSoundResultLabel } from "./status"
import { calculateMarkerPosition } from "./marker"

export function hasDisplayableObservedValue(feature) {
  if (!feature || feature.observed === undefined || feature.observed === null) return false
  const observedText = String(feature.observed).trim()
  return observedText !== "" && observedText !== "—" && observedText.toLowerCase() !== "not assessed"
}

export function displayObservedValue(feature) {
  const observed =
    feature && feature.observed !== undefined && feature.observed !== null ? String(feature.observed) : ""
  if (isRhythmRegularityFeature(feature)) {
    return observed
      .replace(/Expected PCG timing variability/g, "Normal")
      .replace(/Moderate PCG timing variability/g, "Moderate")
      .replace(/High PCG timing variability/g, "Elevated")
      .replace(/Periodic PCG timing pattern/g, "Periodic")
  }
  return observed
}

export function reportCardTitle(feature) {
  if (!feature) return ""
  if (feature.name === "Structural Sound Summary") return "Structural Sound"
  return feature.name
}

export function displayReferenceRange(feature, hrBpm) {
  if (!feature) return ""
  if (isCardiacCycleTimingFeature(feature)) {
    return cardiacCycleRatioZonesForHeartRate(hrBpm).label
  }
  if (isSysDiaDurationFeature(feature)) {
    return expectedSysDiaRangesForHeartRate(hrBpm).label
  }
  return feature.displayReferenceRange || (feature.normalRange ? `Reference Range: ${feature.normalRange}` : "")
}

export function reportResultText(feature, colorClass, { patientFacing = false } = {}) {
  if (!feature) return ""
  if (feature.name === "Timing Summary") return timingSummaryResultLabel(colorClass)
  if (feature.name === "Structural Sound" || feature.name === "Structural Sound Summary")
    return structuralSoundResultLabel(feature, colorClass)
  if (isRhythmRegularityFeature(feature)) return rhythmRegularityDisplayLabel(feature, colorClass)
  if (patientFacing && isCardiacCycleTimingFeature(feature)) {
    return colorClass === "green"
      ? "Normal ratio"
      : colorClass === "amber"
      ? "Borderline ratio"
      : "Outside normal ratio"
  }
  if (patientFacing && isSysDiaDurationFeature(feature)) {
    return colorClass === "green"
      ? "Normal duration pattern"
      : colorClass === "amber"
      ? "Borderline duration pattern"
      : "Shortened filling window"
  }
  if (!feature.normalRange && feature.type !== "bpm" && feature.type !== "ratio")
    return getStatusLabel(feature, colorClass)
  if (hasDisplayableObservedValue(feature)) return feature.observed
  return getStatusLabel(feature, colorClass)
}

export function providerFindingFlagText(feature, colorClass, hrBpm) {
  if (!feature || colorClass === "green") return ""
  // A heart-rate range that crosses reference categories
  const range = heartRateRangeOf(feature)
  if (range && heartRateRangeGrades(range, feature.normalRange, feature.scaleType).mixed) {
    return "Mixed range · reviewed across recordings"
  }
  if (isRhythmRegularityFeature(feature)) return ""
  if (feature.name === "Structural Sound" || feature.name === "Structural Sound Summary") {
    return colorClass === "red" ? "Elevated" : "Borderline"
  }
  if (colorClass === "amber") return "Borderline"
  const markerPos =
    feature.markerPosition !== undefined ? feature.markerPosition : calculateMarkerPosition(feature, hrBpm)
  return feature.scaleType === "unidirectional" || markerPos >= 50 ? "Elevated" : "Low"
}
